<script setup>
// Webchat de continuidade no reply box (.ai/webchat-continuidade-whatsapp-plan.md).
// Duas coisas que o atendente sob volume precisa ver sem abrir menu nenhum:
//   · Enviar link do webchat — manda o link AGORA. É mensagem PAGA no
//     WhatsApp, então passa por confirmação. Com sessão viva é reenvio; a
//     chave só volta pro webchat quando o cliente abre o link ou escreve na tela.
//   · Pra onde vai a próxima resposta — webchat (de graça, tela do cliente)
//     ou WhatsApp (pago, janela de 24h da Meta). O estado é o do servidor:
//     nada de mutação otimista, o backend rebroadcasta a conversa.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { toast } from 'dashboard/helper/toast';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  // Id da conversa que o painel está editando. O compositor de campanha reusa
  // este reply box com `0` — sem casar com a conversa aberta, não mostramos
  // nada (o `getSelectedChat` lá é de outra tela).
  conversationId: {
    type: Number,
    required: true,
  },
  // Nota privada não vai pro cliente por canal nenhum: os dois controles
  // saem de cena pra não sugerir que a nota seria entregue.
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const { t, locale } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const getInbox = useMapGetter('inboxes/getInbox');

const inviteDialog = ref(null);
const whatsappDialog = ref(null);
const isSendingInvite = ref(false);
const isSwitchingChannel = ref(false);

const isCurrentConversation = computed(
  () => !!props.conversationId && currentChat.value?.id === props.conversationId
);

const inbox = computed(
  () => getInbox.value?.(currentChat.value?.inbox_id) || {}
);

const webchat = computed(() => currentChat.value?.webchat || null);

// `webchat_config` fica em snake_case no store de inboxes (o payload da inbox
// não é camelizado — mesma grafia que a tela de configuração da caixa lê).
const isWebchatEnabledOnInbox = computed(
  () =>
    isFeatureEnabledonAccount.value?.(
      currentAccountId.value,
      FEATURE_FLAGS.WEBCHAT
    ) === true && inbox.value?.webchat_config?.enabled === true
);

// Fora da janela de 24h (`can_reply` falso = o banner "só modelo de mensagem")
// o link não sai: o convite é mensagem livre (cta_url) e a Meta só aceita
// template. O atendente manda o template de sempre; quando o cliente
// responder, a janela abre e o link volta a poder ser enviado.
// Em modo web `can_reply` é sempre true (a resposta vai pra tela): aí quem diz
// a janela CRUA da Meta — a mesma que o InviteService confere — é
// `webchat.whatsapp_window_open`. Sem ela o botão ficava ativo e o reenvio 422.
const isInviteBlockedByWindow = computed(
  () =>
    currentChat.value?.can_reply === false ||
    webchat.value?.whatsapp_window_open === false
);

const inviteTooltip = computed(() =>
  isInviteBlockedByWindow.value
    ? t('CONVERSATION.WEBCHAT.SEND_LINK_WINDOW_CLOSED_TOOLTIP')
    : t('CONVERSATION.WEBCHAT.SEND_LINK_TOOLTIP')
);

const showInviteButton = computed(
  () =>
    isCurrentConversation.value &&
    !props.isOnPrivateNote &&
    isWebchatEnabledOnInbox.value
);

// Sem sessão o backend não tem canal pra trocar (404) — o controle só existe
// enquanto a conversa está no webchat. Com link enviado e ainda não aceito
// (`link_pending`: convite nunca aberto, ou reenvio que o cliente não tocou) a
// resposta vai pelo WhatsApp de qualquer jeito (Webchat::Session.web_mode?):
// o seletor marcaria "Webchat" mentindo — o selo do cabeçalho diz o porquê.
const showChannelToggle = computed(
  () =>
    isCurrentConversation.value &&
    !props.isOnPrivateNote &&
    webchat.value?.status === 'active' &&
    !webchat.value?.link_pending
);

const replyChannel = computed(() => webchat.value?.reply_channel || 'webchat');

// A REGRA ("fora da janela só template") já estava no diálogo; faltava o FATO.
// Sem `whatsapp_window_expires_at` (canal sem janela, nenhuma incoming) não
// inventamos prazo: só a regra fica.
const whatsappWindow = computed(() => {
  const info = webchat.value;
  if (!info || info.whatsapp_window_open === undefined) return '';
  if (!info.whatsapp_window_open)
    return t('CONVERSATION.WEBCHAT.WINDOW_CLOSED');
  if (!info.whatsapp_window_expires_at) return '';
  const time = new Date(
    info.whatsapp_window_expires_at * 1000
    // Locale do usuário (o app usa `pt_BR`, o Intl quer `pt-BR`): sem isso o
    // atendente brasileiro lia "01:31 PM".
  ).toLocaleTimeString(locale.value?.replace('_', '-'), {
    hour: '2-digit',
    minute: '2-digit',
  });
  // Janela do anúncio: aberta E de graça (segue de graça depois de 1º/10/2026,
  // quando a resposta dentro da janela comum de 24h passa a ser cobrada).
  return info.whatsapp_window_free
    ? t('CONVERSATION.WEBCHAT.WINDOW_FREE_AD', { time })
    : t('CONVERSATION.WEBCHAT.WINDOW_OPEN', { time });
});

const whatsappDialogDescription = computed(() =>
  [t('CONVERSATION.WEBCHAT.CHANNEL_WHATSAPP_DESCRIPTION'), whatsappWindow.value]
    .filter(Boolean)
    .join(' ')
);

const channelOptions = computed(() => [
  {
    value: 'webchat',
    label: t('CONVERSATION.WEBCHAT.CHANNEL_WEBCHAT'),
    icon: 'i-lucide-link',
    tooltip: t('CONVERSATION.WEBCHAT.CHANNEL_WEBCHAT_TOOLTIP'),
    // Cor = CANAL: azul é a tela (de graça), verde é o WhatsApp (pago). O
    // mesmo par no selo do header — um código só pros dois lugares.
    // `n-iris` é o azul: `n-blue` neste fork é a rampa LIMA da marca.
    activeClass: 'bg-n-iris-3 ring-1 ring-n-iris-9 text-n-iris-12',
  },
  {
    value: 'whatsapp',
    label: t('CONVERSATION.WEBCHAT.CHANNEL_WHATSAPP'),
    icon: 'i-ph-whatsapp-logo',
    // Só o custo: o prazo da janela de 24h fica no diálogo de confirmação.
    tooltip: t('CONVERSATION.WEBCHAT.CHANNEL_WHATSAPP_TOOLTIP'),
    activeClass: 'bg-n-green-3 ring-1 ring-n-green-9 text-n-green-12',
  },
]);

const describeError = error =>
  error?.response?.data?.error || error?.message || String(error);

const sendInvite = async () => {
  if (isSendingInvite.value) return;
  isSendingInvite.value = true;
  // toast.promise do vue-sonner devolve o id do toast, não a promise: com
  // `await` nele o diálogo fecharia com o POST ainda em voo e o 2º clique
  // mandaria outra mensagem paga. Espera-se a MESMA promise à parte.
  const request = ConversationApi.webchatInvite(props.conversationId);
  toast.promise(request, {
    loading: t('CONVERSATION.WEBCHAT.INVITE_SENDING'),
    success: t('CONVERSATION.WEBCHAT.INVITE_SENT'),
    error: error =>
      t('CONVERSATION.WEBCHAT.INVITE_ERROR', { error: describeError(error) }),
  });
  try {
    await request;
  } catch {
    // Já divulgado pelo toast de erro acima.
  } finally {
    isSendingInvite.value = false;
    inviteDialog.value?.close();
  }
};

const applyReplyChannel = async channel => {
  if (isSwitchingChannel.value) return;
  isSwitchingChannel.value = true;
  const request = ConversationApi.webchatReplyChannel(
    props.conversationId,
    channel
  );
  toast.promise(request, {
    loading: t('CONVERSATION.WEBCHAT.CHANNEL_SWITCHING'),
    success:
      channel === 'whatsapp'
        ? t('CONVERSATION.WEBCHAT.CHANNEL_NOW_WHATSAPP')
        : t('CONVERSATION.WEBCHAT.CHANNEL_NOW_WEBCHAT'),
    error: error =>
      t('CONVERSATION.WEBCHAT.CHANNEL_ERROR', { error: describeError(error) }),
  });
  try {
    await request;
  } catch {
    // Já divulgado pelo toast de erro acima. O selo só muda quando o backend
    // rebroadcasta a conversa — errado é fingir que mudou.
  } finally {
    isSwitchingChannel.value = false;
    whatsappDialog.value?.close();
  }
};

// São dois botões de alternar (`aria-pressed`), não um radiogroup: no radiogroup
// a seta SELECIONA, e aqui selecionar o WhatsApp abre confirmação de mensagem
// paga — navegar com o teclado não pode disparar isso. Cada botão é um tab stop.

const selectReplyChannel = channel => {
  if (isSwitchingChannel.value || channel === replyChannel.value) return;
  // Voltar pro webchat é de graça: vai direto. Ir pro WhatsApp custa e pode
  // esbarrar na janela de 24h — confirma.
  if (channel === 'whatsapp') {
    whatsappDialog.value?.open();
    return;
  }
  applyReplyChannel('webchat');
};
</script>

<template>
  <div
    v-if="showInviteButton || showChannelToggle"
    class="flex items-center gap-2"
  >
    <!-- Tooltip no wrapper: botão desabilitado não dispara hover. -->
    <span v-if="showInviteButton" v-tooltip.top-end="inviteTooltip">
      <Button
        icon="i-lucide-send-horizontal"
        slate
        faded
        sm
        :is-loading="isSendingInvite"
        :disabled="isSendingInvite || isInviteBlockedByWindow"
        :aria-label="inviteTooltip"
        @click="inviteDialog?.open()"
      />
    </span>
    <div
      v-if="showChannelToggle"
      role="group"
      :aria-label="t('CONVERSATION.WEBCHAT.CHANNEL_GROUP_LABEL')"
      class="inline-flex items-center p-0.5 rounded-lg border border-n-weak"
    >
      <button
        v-for="option in channelOptions"
        :key="option.value"
        v-tooltip.top-end="option.tooltip"
        type="button"
        :aria-pressed="replyChannel === option.value"
        :disabled="isSwitchingChannel"
        class="inline-flex items-center h-6 gap-1 px-2 text-xs transition-colors rounded-md disabled:cursor-not-allowed disabled:opacity-50"
        :class="[
          replyChannel !== option.value && 'text-n-slate-11 hover:bg-n-alpha-2',
          replyChannel === option.value &&
            `${option.activeClass} font-semibold`,
        ]"
        @click="selectReplyChannel(option.value)"
      >
        <span class="size-3.5 shrink-0" :class="option.icon" />
        <!-- Os DOIS rótulos ficam visíveis: com o inativo só de ícone o
             atendente lia dois glifos sem palavra, e a largura do chip mudava
             (30↔86px) a cada troca, fazendo a fileira refluir. -->
        {{ option.label }}
      </button>
    </div>
    <Dialog
      ref="inviteDialog"
      type="edit"
      :title="t('CONVERSATION.WEBCHAT.SEND_LINK_TITLE')"
      :description="t('CONVERSATION.WEBCHAT.SEND_LINK_DESCRIPTION')"
      :confirm-button-label="t('CONVERSATION.WEBCHAT.SEND_LINK_CONFIRM')"
      :is-loading="isSendingInvite"
      @confirm="sendInvite"
    />
    <Dialog
      ref="whatsappDialog"
      type="edit"
      :title="t('CONVERSATION.WEBCHAT.CHANNEL_WHATSAPP_TITLE')"
      :description="whatsappDialogDescription"
      :confirm-button-label="t('CONVERSATION.WEBCHAT.CHANNEL_WHATSAPP_CONFIRM')"
      :is-loading="isSwitchingChannel"
      @confirm="applyReplyChannel('whatsapp')"
    />
  </div>
</template>
